"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import ProductPlaceholder from "./ProductPlaceholder";
import PhotoFrame from "./PhotoFrame";

export interface RoutineStep {
  n: string;
  label: string;
  prod: {
    code: string;
    sku: string;
    name: string;
    href: string;
    image?: string;
    time: string;
  };
  note: string;
  tone: "light" | "dark" | "mid" | "bright" | "macro";
}

interface RoutineSystemProps {
  steps: RoutineStep[];
  ctaHref: string;
  ctaLabel?: string;
  sectionLabel?: string;
  i18n?: {
    headlineLead?: string;          // "Five steps."
    headlineEm?: string;            // "One routine."
    body?: string;                  // intro paragraph
    stepLabel?: string;             // "STEP" prefix
    viewProductLabel?: string;      // "VIEW PRODUCT ↗"
  };
}

export default function RoutineSystem({
  steps,
  ctaHref,
  ctaLabel = "FIND MY ROUTINE →",
  sectionLabel = "04 / THE 5-STEP SYSTEM",
  i18n,
}: RoutineSystemProps) {
  const t = {
    headlineLead: i18n?.headlineLead ?? "Five steps.",
    headlineEm: i18n?.headlineEm ?? "One routine.",
    body: i18n?.body ?? "The BP Derma 5-Step System — cleanse, rebalance, focus, boost, protect — developed as a framework dermatologists can prescribe and clients can follow without a chart.",
    stepLabel: i18n?.stepLabel ?? "STEP",
    viewProductLabel: i18n?.viewProductLabel ?? "VIEW PRODUCT ↗",
  };
  const [active, setActive] = useState(0);
  const s = steps[active];

  return (
    <section
      style={{
        padding: "96px var(--bp-edge)",
        borderBottom: "1px solid var(--bp-rule)",
        background: "var(--bp-bg-2)",
      }}
    >
      <div className="bp-routine-grid">
        <div>
          <div className="bp-mono"><span className="bp-bracket">{sectionLabel}</span></div>
          <h2
            style={{
              fontFamily: "var(--bp-serif)",
              fontWeight: 300,
              letterSpacing: "-0.02em",
              fontSize: "clamp(36px, 4.6vw, 64px)",
              lineHeight: 1.02,
              margin: "20px 0 16px",
            }}
          >
            {t.headlineLead}
            <br />
            <em style={{ fontStyle: "italic", color: "var(--bp-ink-2)" }}>{t.headlineEm}</em>
          </h2>
          <p style={{ color: "var(--bp-ink-2)", maxWidth: 420 }}>
            {t.body}
          </p>

          <div style={{ marginTop: 40, borderTop: "1px solid var(--bp-rule)" }}>
            {steps.map((step, i) => (
              <button
                key={step.n}
                onClick={() => setActive(i)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "48px 160px 1fr auto",
                  gap: 16,
                  width: "100%",
                  textAlign: "left",
                  padding: "18px 0",
                  alignItems: "center",
                  color: i === active ? "var(--bp-ink)" : "var(--bp-ink-3)",
                  background: "none",
                  borderTopWidth: 0,
                  borderRightWidth: 0,
                  borderLeftWidth: 0,
                  borderBottomWidth: 1,
                  borderBottomStyle: "solid",
                  borderBottomColor: "var(--bp-rule)",
                  cursor: "pointer",
                }}
              >
                <span className="bp-mono" style={{ color: "inherit" }}>{step.n}</span>
                <span style={{ fontFamily: "var(--bp-serif)", fontSize: 20, fontWeight: 300 }}>
                  {step.label}
                </span>
                <span style={{ fontSize: 13, color: "inherit" }}>{step.prod.name}</span>
                <span style={{ fontFamily: "var(--bp-mono)", fontSize: 11, color: "inherit" }}>
                  {i === active ? "●" : "○"}
                </span>
              </button>
            ))}
          </div>
          <Link href={ctaHref} className="bp-btn" style={{ marginTop: 32, display: "inline-flex" }}>
            {ctaLabel}
          </Link>
        </div>

        <div
          style={{
            position: "relative",
            background: "var(--bp-bg)",
            border: "1px solid var(--bp-rule)",
            padding: 32,
            display: "flex",
            flexDirection: "column",
            gap: 32,
            minHeight: 560,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 16,
              alignItems: "start",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <span className="bp-mono-sm">
                {t.stepLabel} {s.n} — {s.label}
              </span>
              <div
                style={{
                  fontFamily: "var(--bp-serif)",
                  fontSize: "clamp(28px, 3.2vw, 40px)",
                  fontWeight: 300,
                  letterSpacing: "-0.02em",
                  marginTop: 12,
                  lineHeight: 1.02,
                  textWrap: "balance" as never,
                }}
              >
                {s.prod.name}
              </div>
              <div className="bp-mono" style={{ marginTop: 12 }}>{s.prod.code}</div>
            </div>
            <span className="bp-mono-sm" style={{ whiteSpace: "nowrap" }}>{s.prod.time}</span>
          </div>

          <div
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              alignItems: "stretch",
            }}
          >
            <div
              style={{
                background: "var(--bp-bg-2)",
                position: "relative",
                overflow: "hidden",
                aspectRatio: "1/1",
              }}
            >
              {s.prod.image ? (
                <Image
                  src={s.prod.image}
                  alt={s.prod.name}
                  fill
                  sizes="(max-width:900px) 100vw, 25vw"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <ProductPlaceholder label={s.prod.code} />
              )}
            </div>
            <div>
              <PhotoFrame tone={s.tone} ratio="1/1" label={`APPLIC. ${s.n}`} />
            </div>
          </div>

          <div
            style={{
              borderTop: "1px solid var(--bp-rule)",
              paddingTop: 20,
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <p style={{ margin: 0, fontSize: 14, color: "var(--bp-ink-2)", maxWidth: 340 }}>
              {s.note}
            </p>
            <Link
              href={s.prod.href}
              className="bp-mono-sm"
              style={{
                borderBottom: "1px solid var(--bp-ink)",
                paddingBottom: 2,
                color: "var(--bp-ink)",
              }}
            >
              {t.viewProductLabel}
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        .bp-routine-grid {
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 56px;
        }
        @media (max-width: 900px) {
          .bp-routine-grid {
            grid-template-columns: 1fr;
            gap: 32px;
          }
        }
      `}</style>
    </section>
  );
}
